/**
 * Algorithm Registry Service — wraps /api/admin/ai/algorithms/
 * Phase 4.8-B — WQ-198
 */

// === Types ===

import type { ComputeTierValue, CompatibilityResult } from './computeCapabilityService';

export interface Algorithm {
  id: string;
  name: string;
  category: string;
  description: string | null;
  problem_type: string | null;
  compute_profile: string | null;
  // WQ-201: compute tier metadata
  compute_tier?: ComputeTierValue;
  cpu_training_limit_rows?: number | null;
  onnx_inference_supported?: boolean;
  current_compatibility?: CompatibilityResult | null;
  status: string;
  hyperparameter_schema: Record<string, unknown> | null;
  data_requirements: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface CategorySummary {
  category: string;
  count: number;
  algorithms: string[];
}

export interface CandidateSearch {
  problem_type?: string;
  data_size?: string;
  compute_budget?: string;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function listAlgorithms(
  authFetch: FetchFn,
  filters?: { category?: string; status?: string; problem_type?: string; compute_profile?: string }
): Promise<Algorithm[]> {
  const params = new URLSearchParams();
  if (filters?.category) params.set('category', filters.category);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.problem_type) params.set('problem_type', filters.problem_type);
  if (filters?.compute_profile) params.set('compute_profile', filters.compute_profile);
  const qs = params.toString();
  const res = await authFetch(`/api/admin/ai/algorithms/${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list algorithms: ${res.status}`);
  return res.json();
}

export async function getAlgorithm(authFetch: FetchFn, id: string): Promise<Algorithm> {
  const res = await authFetch(`/api/admin/ai/algorithms/${id}`);
  if (!res.ok) throw new Error(`Failed to get algorithm: ${res.status}`);
  return res.json();
}

export async function getCategoriesSummary(authFetch: FetchFn): Promise<CategorySummary[]> {
  const res = await authFetch('/api/admin/ai/algorithms/categories/summary');
  if (!res.ok) throw new Error(`Failed to get categories: ${res.status}`);
  return res.json();
}

export async function searchCandidates(
  authFetch: FetchFn,
  criteria: CandidateSearch
): Promise<Algorithm[]> {
  const res = await authFetch('/api/admin/ai/algorithms/search/candidates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(criteria),
  });
  if (!res.ok) throw new Error(`Failed to search candidates: ${res.status}`);
  return res.json();
}

// === WQ-242 — Register support ===

/** Mirror of platform-api APPROVED_LICENSES (WQ-242). Source of truth: algorithm_registry_service.py. */
export const APPROVED_LICENSES: ReadonlySet<string> = new Set([
  'mit', 'apache-2.0', 'bsd-2-clause', 'bsd-3-clause',
  'isc', 'unlicense', 'cc0-1.0', 'zlib',
]);

/** Mirror of platform-api REJECTED_LICENSES (WQ-242). Source of truth: algorithm_registry_service.py. */
export const REJECTED_LICENSES: ReadonlySet<string> = new Set([
  'gpl-2.0', 'gpl-3.0', 'agpl-3.0', 'lgpl-2.1', 'lgpl-3.0',
  'mpl-2.0', 'epl-1.0', 'epl-2.0', 'cc-by-sa-4.0', 'cc-by-sa-3.0',
  'cddl-1.0', 'osl-3.0', 'cc-by-nc-4.0', 'cc-by-nc-sa-4.0',
]);

/** Mirror of platform-api BLOCKED_COUNTRIES (WQ-242). Source of truth: app/core/model_origins.py. */
export const BLOCKED_ORIGIN_COUNTRIES: ReadonlySet<string> = new Set([
  'CN', 'RU', 'IR', 'KP', 'BY', 'SY',
]);

export type LicenseVerdict = 'approved' | 'flagged' | 'rejected';

export function classifyLicense(licenseId: string): LicenseVerdict {
  const normalized = licenseId.trim().toLowerCase();
  if (normalized.length === 0) return 'flagged';
  if (REJECTED_LICENSES.has(normalized)) return 'rejected';
  if (APPROVED_LICENSES.has(normalized)) return 'approved';
  return 'flagged';
}

export interface AlgorithmRegisterRequest {
  name: string;
  slug?: string;
  category: string;
  description?: string;
  library: string;
  library_version?: string;
  adapter_class?: string;
  problem_types: string[];
  data_requirements?: Record<string, unknown>;
  hyperparameter_schema?: Record<string, unknown>;
  evaluation_metrics?: string[];
  interpretability_level: string;
  compute_profile: string;
  supports_incremental?: boolean;
  supports_feature_importance?: boolean;
  supports_probability_output?: boolean;
  license: string;
  origin_country?: string;
  tenant_id?: string;
  phase_available: string;
  is_active?: boolean;
}

export async function registerAlgorithm(
  authFetch: FetchFn,
  request: AlgorithmRegisterRequest,
): Promise<Algorithm> {
  const res = await authFetch('/api/admin/ai/algorithms/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    let detail: string;
    try {
      const body = await res.json();
      detail = body?.detail ?? `HTTP ${res.status}`;
    } catch {
      detail = `HTTP ${res.status}`;
    }
    throw new Error(detail);
  }
  return res.json();
}

export async function updateAlgorithm(
  authFetch: FetchFn,
  id: string,
  data: Partial<Algorithm>
): Promise<Algorithm> {
  const res = await authFetch(`/api/admin/ai/algorithms/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update algorithm: ${res.status}`);
  return res.json();
}

export async function deactivateAlgorithm(authFetch: FetchFn, id: string): Promise<Algorithm> {
  const res = await authFetch(`/api/admin/ai/algorithms/${id}/deactivate`, { method: 'PATCH' });
  if (!res.ok) throw new Error(`Failed to deactivate algorithm: ${res.status}`);
  return res.json();
}

export async function activateAlgorithm(authFetch: FetchFn, id: string): Promise<Algorithm> {
  const res = await authFetch(`/api/admin/ai/algorithms/${id}/activate`, { method: 'PATCH' });
  if (!res.ok) throw new Error(`Failed to activate algorithm: ${res.status}`);
  return res.json();
}
